import * as React from 'react';

import { useTranslations } from '@rematter/modules/settings/i18n/useTranslations';
import { ItemRecord } from '@rematter/modules/settings/types/inventoryItem';
import { DataGridColumn, LocalizedDate } from '@rematter/ui-web';

type Props = {
  onRowClick: (row: ItemRecord) => void;
};

export const useItemsColumns = ({ onRowClick }: Props) => {
  const t = useTranslations();

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: ItemRecord }) => {
      onRowClick(row);
    },
    [onRowClick],
  );

  const columns: DataGridColumn<ItemRecord>[] = React.useMemo(
    () => [
      {
        key: 'name',
        header: t.inventoryView.itemsPanel.columns.name,
        sortable: true,
        width: 200,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left font-medium">
            {row.name}
          </div>
        ),
      },
      {
        key: 'skuNumber',
        header: t.inventoryView.itemsPanel.columns.sku,
        sortable: true,
        width: 150,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.skuNumber}</span>
        ),
      },
      {
        key: 'category',
        header: t.inventoryView.itemsPanel.columns.category,
        width: 150,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => <span>{row.category?.name}</span>,
      },
      {
        key: 'itemGroup',
        header: t.inventoryView.itemsPanel.columns.itemGroup,
        width: 150,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => <span>{row.itemGroup?.name}</span>,
      },
      {
        key: 'createdAt',
        header: t.inventoryView.itemsPanel.columns.created,
        sortable: true,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
    ],
    [t, handleCellDoubleClick],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
